import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowPathIcon,
  ChatBubbleLeftEllipsisIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { getRouteApi } from '@tanstack/react-router'
import { Button, DataTable, DatePicker, Input, OverflowText, PageHeader } from '@/shared/ui'
import type { ApiError, Column, OrderListItem, OrderListRequest } from '@/shared/api'
import { useDebounce, useOrderFilters, useOrders } from '@/shared/hooks'
import { useAuth } from '@/shared/contexts'
import { OrderStatusBadge } from './OrderStatusBadge'
import { OrderDrawer } from './OrderDrawer'
import {
  dateToIsoDate,
  isoDateToDate,
  todayIsoDateMsk,
  type OrdersDateType,
  type OrdersSearch,
  type OrdersTab,
} from './ordersSearch'

const ordersRoute = getRouteApi('/orders')

const PAGE_SIZE = 50

const TABS: Array<{ value: OrdersTab; label: string }> = [
  { value: 'active', label: 'Активные' },
  { value: 'closed', label: 'Завершённые' },
]

const DATE_TYPE_OPTIONS: Array<{ value: OrdersDateType; label: string }> = [
  { value: 'delivery', label: 'По дате доставки' },
  { value: 'creation', label: 'По дате создания' },
]

/**
 * Дата из календаря → Unix-секунды начала дня МСК.
 * Backend ждёт date_from/date_to как начало московских суток; date_to
 * включает весь день (`< date_to + 86400`), поэтому конец суток не нужен.
 */
const dayStartMsk = (date: Date): number =>
  Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 1000) - 3 * 3600

const formatMoney = (value: string | null | undefined): string => {
  if (!value) return '—'
  const amount = Number(value)
  if (Number.isNaN(amount)) return value
  return `${amount.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ₽`
}

const ORDERS_ERROR_FALLBACK = 'Не удалось загрузить заказы'

// Interceptor API уже приводит ошибку к ApiError с backend-полем detail;
// дополнительно читаем detail из исходного ответа на случай другого клиента.
// Технические сообщения axios не показываем пользователю.
const isTechnicalErrorMessage = (message: string): boolean =>
  /^(Request failed with status code|Network Error|timeout of\s)/i.test(
    message.trim()
  )

const getOrdersErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return ORDERS_ERROR_FALLBACK

  const apiError = error as Partial<ApiError> & {
    response?: { data?: { detail?: unknown } }
  }

  const responseDetail = apiError.response?.data?.detail
  if (typeof responseDetail === 'string' && responseDetail.trim()) {
    return responseDetail
  }

  const detail = apiError.detail
  if (
    typeof detail === 'string'
    && detail.trim()
    && !isTechnicalErrorMessage(detail)
  ) {
    return detail
  }

  return ORDERS_ERROR_FALLBACK
}

export const OrdersPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const canViewCard = hasPermission('orders', 'view_card')

  // Источник истины для фильтров — URL (см. ordersSearch.ts), а не useState:
  // F5 и переход по ссылке сохраняют выборку.
  const urlSearch = ordersRoute.useSearch()
  const navigate = ordersRoute.useNavigate()

  const tab = urlSearch.tab ?? 'active'
  const dateType = urlSearch.dateType ?? 'delivery'
  const page = urlSearch.page ?? 1
  const status = urlSearch.status !== undefined ? String(urlSearch.status) : ''
  const deliveryType = urlSearch.deliveryType ?? ''
  const courierId =
    urlSearch.courierId !== undefined ? String(urlSearch.courierId) : ''
  // Список по умолчанию открыт на сегодняшний день. Дефолт ставим только
  // когда в URL нет ни одной даты: отсутствующая граница при уже выбранной
  // второй — это открытый диапазон, а не «сегодня». Подстановка идёт прямо
  // в рендере (URL остаётся источником истины, флага «даты не выбраны» нет),
  // поэтому первый же запрос уходит за сегодня, без выборки «за всё время».
  const todayIso = todayIsoDateMsk()
  const bothDatesMissing = !urlSearch.dateFrom && !urlSearch.dateTo
  const defaultDateIso = bothDatesMissing ? todayIso : undefined
  const dateFrom = isoDateToDate(urlSearch.dateFrom ?? defaultDateIso)
  const dateTo = isoDateToDate(urlSearch.dateTo ?? defaultDateIso)
  const urlQuery = urlSearch.search ?? ''

  // Поле поиска нельзя жёстко привязать к URL: дебаунс «съедал» бы символы.
  // Держим черновик локально, а в URL пишем после паузы. lastAppliedQuery
  // не даёт устаревшему черновику вернуть только что сброшенный фильтр.
  const [searchDraft, setSearchDraft] = useState(urlQuery)
  const debouncedSearch = useDebounce(searchDraft, 500)
  const lastAppliedQuery = useRef(urlQuery)

  const dateFromValue = dateFrom ? dayStartMsk(dateFrom) : undefined
  const dateToValue = dateTo ? dayStartMsk(dateTo) : undefined
  const dateRangeInvalid =
    dateFromValue !== undefined
    && dateToValue !== undefined
    && dateFromValue > dateToValue

  // Любое изменение фильтра сбрасывает страницу, чтобы не остаться
  // на несуществующей странице новой выборки.
  const applyFilters = useCallback(
    (patch: Partial<OrdersSearch>) => {
      navigate({
        search: (prev) => ({ ...prev, ...patch, page: undefined }),
        replace: true,
      })
    },
    [navigate]
  )

  // Подставленный дефолт переносим в URL, чтобы адрес сразу описывал выборку:
  // F5 и ссылка открывают тот же сегодняшний день. replace — чтобы возврат
  // «назад» не проходил через служебный переход. Есть хотя бы одна дата в URL —
  // это уже осознанный выбор (в том числе открытый диапазон), не трогаем.
  useEffect(() => {
    if (!bothDatesMissing) return

    navigate({
      search: (prev) => ({ ...prev, dateFrom: todayIso, dateTo: todayIso }),
      replace: true,
    })
  }, [bothDatesMissing, todayIso, navigate])

  // Черновик поиска → URL после паузы в 500 мс.
  useEffect(() => {
    const next = debouncedSearch.trim()
    if (next === lastAppliedQuery.current) return

    lastAppliedQuery.current = next
    navigate({
      search: (prev) => ({ ...prev, search: next || undefined, page: undefined }),
      replace: true,
    })
  }, [debouncedSearch, navigate])

  // URL → черновик: back/forward и внешние переходы.
  useEffect(() => {
    if (urlQuery === lastAppliedQuery.current) return

    lastAppliedQuery.current = urlQuery
    setSearchDraft(urlQuery)
  }, [urlQuery])

  const params = useMemo<OrderListRequest>(() => {
    const value: OrderListRequest = { page, limit: PAGE_SIZE, tab, dateType }

    if (urlSearch.search) value.search = urlSearch.search
    if (urlSearch.status !== undefined) value.status = urlSearch.status
    if (urlSearch.deliveryType) value.deliveryType = urlSearch.deliveryType
    if (urlSearch.courierId !== undefined) value.courierId = urlSearch.courierId
    if (dateFromValue !== undefined) value.dateFrom = dateFromValue
    if (dateToValue !== undefined) value.dateTo = dateToValue

    return value
  }, [
    page,
    tab,
    dateType,
    urlSearch.search,
    urlSearch.status,
    urlSearch.deliveryType,
    urlSearch.courierId,
    dateFromValue,
    dateToValue,
  ])

  const { data, isLoading, isError, error, refetch } = useOrders(params, {
    enabled: !dateRangeInvalid,
  })
  const { data: filters } = useOrderFilters()

  const handleTabChange = (next: OrdersTab) => {
    if (next === tab) return
    navigate({
      search: (prev) => ({
        ...prev,
        tab: next === 'active' ? undefined : next,
        // Статусы активных и завершённых не пересекаются: оставленный статус
        // дал бы гарантированно пустой список на другой вкладке.
        status: undefined,
        page: undefined,
      }),
      replace: true,
    })
  }

  const handlePageChange = (next: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: next > 1 ? next : undefined }),
      replace: true,
    })
  }

  const handleReset = () => {
    lastAppliedQuery.current = ''
    setSearchDraft('')
    // Сброс возвращает к сегодняшнему дню, а не ко всей истории, — тем же
    // значениям, с которыми страница открывается по умолчанию.
    navigate({
      search: { dateFrom: todayIso, dateTo: todayIso },
      replace: true,
    })
  }

  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)

  const columns: Column<OrderListItem>[] = [
    {
      key: 'shopOrderId',
      title: '№ заказа',
      width: '11%',
      render: (_, item) => (
        <div className="space-y-0.5">
          <OverflowText
            text={String(item.number || item.shopOrderId)}
            className="text-sm font-medium leading-tight text-gray-900"
          />
          <OverflowText
            text={`R-CRM: ${item.externalOrderId || '—'}`}
            className="text-xs leading-tight text-gray-500"
          />
        </div>
      ),
    },
    {
      key: 'legalEntityName',
      title: 'Источник / юрлицо',
      width: '11%',
      render: (_, item) => (
        <div className="space-y-0.5">
          <OverflowText
            text={item.sourceLabel || item.source || '—'}
            className="text-sm leading-tight text-gray-900"
          />
          <OverflowText
            text={item.legalEntityName || '—'}
            className="text-xs leading-tight text-gray-500"
          />
        </div>
      ),
    },
    {
      key: 'deliveryDate',
      title: 'Дата доставки',
      width: '12%',
      render: (_, item) => (
        <div className="space-y-0.5">
          <div className="text-sm leading-tight text-gray-900">
            {item.deliveryDateFormatted || '—'}
          </div>
          {/* Интервал не сокращаем: логисту важны обе границы окна доставки,
              поэтому при нехватке места он переносится, а не обрезается. */}
          {item.deliveryInterval?.label && (
            <div className="text-xs leading-tight text-gray-500 break-words">
              {item.deliveryInterval.label}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'address',
      title: 'Адрес',
      width: '18%',
      render: (_, item) => (
        <OverflowText
          text={item.address || '—'}
          className="text-sm leading-tight text-gray-900"
        />
      ),
    },
    {
      key: 'status',
      title: 'Статус',
      width: '13%',
      render: (_, item) => (
        // Бейдж отдаём как есть, но inline-block: тогда переполнение ловится
        // измерением превью, а при нехватке места появляется и подсказка.
        <OverflowText text={item.statusText?.trim() || ''}>
          <OrderStatusBadge
            status={item.status}
            statusText={item.statusText}
            className="!inline-block"
          />
        </OverflowText>
      ),
    },
    {
      key: 'deliveryType',
      title: 'Тип доставки',
      width: '7%',
      render: (_, item) => (
        <OverflowText
          text={item.deliveryTypeName || item.deliveryType || '—'}
          className="text-sm leading-tight text-gray-900"
        />
      ),
    },
    {
      key: 'courierId',
      title: 'Курьер',
      width: '10%',
      render: (_, item) => (
        <div className="space-y-0.5">
          <OverflowText
            text={item.courierName || '—'}
            className="text-sm leading-tight text-gray-900"
          />
          {item.courierPhone && (
            <OverflowText
              text={item.courierPhone}
              className="text-xs leading-tight text-gray-500"
            />
          )}
        </div>
      ),
    },
    {
      key: 'clientPrice',
      title: 'Сумма',
      width: '11%',
      render: (_, item) => (
        // Значение не переносится; если сумма не влезает в колонку —
        // обрезается многоточием, точная сумма есть в подсказке.
        <OverflowText
          text={formatMoney(item.clientPrice)}
          className="text-sm leading-tight text-gray-900 whitespace-nowrap"
        />
      ),
    },
    {
      key: 'comment',
      title: 'Комментарий',
      width: '7%',
      // Заголовок «Комментарий» в узкую колонку не влезает: показываем иконку,
      // текст остаётся в aria-label и в подсказке.
      headerIcon: <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />,
      render: (_, item) =>
        item.comment ? (
          <OverflowText
            text={item.comment}
            className="flex text-gray-500"
            alwaysShowTooltip
          >
            {/* role="img" нужен, потому что heroicons помечает svg aria-hidden:
                без него подпись не озвучивается скринридером. */}
            <span
              role="img"
              aria-label={`Комментарий: ${item.comment}`}
              className="inline-flex"
            >
              <ChatBubbleLeftEllipsisIcon className="h-5 w-5" />
            </span>
          </OverflowText>
        ) : (
          '—'
        ),
    },
  ]

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        title="Заказы"
        description="Оперативный контроль доставок"
      />

      <div className="p-6 space-y-4">
        {/* Вкладки активных и завершённых заказов */}
        <div className="flex flex-wrap gap-2">
          {TABS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={
                tab === item.value
                  ? 'admin-button admin-button-primary'
                  : 'admin-button admin-button-secondary'
              }
              onClick={() => handleTabChange(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Фильтры */}
        <div className="admin-card p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-64">
              <Input
                label="Поиск"
                placeholder="Номер, R-CRM ID, адрес, получатель..."
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
            </div>

            <div className="w-48 space-y-1">
              <label className="block text-sm font-medium text-gray-700">Статус</label>
              <select
                className="admin-input"
                value={status}
                onChange={(e) =>
                  applyFilters({
                    status: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
              >
                <option value="">Все статусы</option>
                {(filters?.statuses ?? []).map((option) => (
                  <option key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-52 space-y-1">
              <label className="block text-sm font-medium text-gray-700">Тип доставки</label>
              <select
                className="admin-input"
                value={deliveryType}
                onChange={(e) =>
                  applyFilters({ deliveryType: e.target.value || undefined })
                }
              >
                <option value="">Все типы</option>
                {(filters?.deliveryTypes ?? []).map((option) => (
                  <option key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-56 space-y-1">
              <label className="block text-sm font-medium text-gray-700">Курьер</label>
              <select
                className="admin-input"
                value={courierId}
                onChange={(e) =>
                  applyFilters({
                    courierId: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
              >
                <option value="">Все курьеры</option>
                {(filters?.couriers ?? []).map((option) => (
                  <option key={option.userId} value={String(option.userId)}>
                    {option.name || option.phone || `Курьер #${option.userId}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-48 space-y-1">
              <label className="block text-sm font-medium text-gray-700">Тип даты</label>
              <select
                className="admin-input"
                value={dateType}
                onChange={(e) =>
                  applyFilters({
                    dateType:
                      e.target.value === 'delivery'
                        ? undefined
                        : (e.target.value as OrdersDateType),
                  })
                }
              >
                {DATE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex w-44 flex-col gap-1">
                  <label className="text-xs text-gray-500">Дата с</label>
                  <DatePicker
                    value={dateFrom}
                    onChange={(date) =>
                      // «Очистить» в календаре (date = null) убирает границу
                      // из URL: диапазон становится открытым с этой стороны,
                      // а не «сегодня-сегодня».
                      applyFilters({ dateFrom: dateToIsoDate(date) })
                    }
                  />
                </div>

                <div className="flex w-44 flex-col gap-1">
                  <label className="text-xs text-gray-500">Дата по</label>
                  <DatePicker
                    value={dateTo}
                    onChange={(date) =>
                      applyFilters({ dateTo: dateToIsoDate(date) })
                    }
                  />
                </div>
              </div>
              {dateRangeInvalid && (
                <p className="text-sm text-error-600">
                  Дата «с» не может быть позже даты «по»
                </p>
              )}
            </div>

            <Button variant="secondary" onClick={handleReset}>
              <ArrowPathIcon className="mr-2 h-4 w-4" />
              Сбросить
            </Button>
          </div>
        </div>

        {dateRangeInvalid ? (
          <div className="admin-card p-8 text-center text-sm text-gray-500">
            Укажите корректный диапазон дат, чтобы загрузить заказы
          </div>
        ) : isError ? (
          <div className="admin-card p-8 text-center">
            <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-error-500" />
            <h3 className="mt-3 text-lg font-medium text-gray-900">
              Не удалось загрузить заказы
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {getOrdersErrorMessage(error)}
            </p>
            <Button className="mt-4" variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
          </div>
        ) : (
          <>
            {data && (
              <p className="text-sm text-gray-500">
                Найдено заказов: {data.total}
              </p>
            )}
            <DataTable
              data={data?.items ?? []}
              columns={columns}
              loading={isLoading}
              dense
              fixedLayout
              emptyMessage="Заказов с выбранными фильтрами нет"
              {...(canViewCard
                ? {
                    onRowClick: (item: OrderListItem) =>
                      setSelectedOrderId(item.shopOrderId),
                  }
                : {})}
              pagination={data ? {
                page: data.page,
                limit: data.limit,
                total: data.total,
                pages: data.pages,
                onPageChange: handlePageChange,
              } : undefined}
            />
          </>
        )}
      </div>

      {selectedOrderId !== null && canViewCard && (
        <OrderDrawer
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
        />
      )}
    </div>
  )
}
